import { GraphQLError } from "graphql"

import { EinzaGraphiQLService } from "@/services/einzaGraphiQL"
import { AppSettings } from "@/settings"

export const mutationUpdateReservation = {
  async updateReservation(_parent, args, context, _info) {
    const { lang, cdn, orderNumber, values } = args
    const { user, sanityClient: client } = context

    if (!user?.allowReservationMutation) {
      throw new GraphQLError("Reservation action is not allowed for this user", {
        extensions: {
          code: "NOT_ALLOWED",
        },
      })
    }

    const config = AppSettings.sanityConfigByLang(lang)
    client.config({ ...config, useCdn: cdn })

    const activeReservation = await getReservationFromArchive({ orderNumber })
    if (!activeReservation) {
      throw new Error("RESERVATION_NOT_FOUND")
    }

    validateChanges(
      activeReservation.validTill,
      values.validTill,
      activeReservation.items,
      values.articleData,
      values.hasDraftItems,
    )

    const doc = {
      _type: "reservation",
      customerNumber: activeReservation.customerNumber,
      lineItems: values.articleData.map((item) => {
        return {
          position: Number(item.posId),
          sku: item.number,
          quantity: item.amount,
          _key: item.posId,
        }
      }),
      modificationSource: "Sanity",
      admNumber: user.employeeNumber,
      documentType: "reservation",
      reservationNumber: activeReservation.orderNumber,
      validTill: new Date(values.validTill).toISOString().split("T")[0],
    }

    const insertReservationUpdate = await client.create(doc)
    return { id: insertReservationUpdate?._id }
  },
}

/**
 * @param {{orderNumber: string}}
 * @returns {object}
 */
const getReservationFromArchive = async ({ orderNumber }) => {
  const graphiQLQueryObject = [
    {
      method: "findOrder",
      variables: { orderNumber },
      fields: ["orderNumber", "customerNumber", "orderType", "orderNumber2", "admNumber", "validTill"],
      expandedFields: {
        items: ["amount", "posId"],
      },
    },
  ]

  const mappedQuery = EinzaGraphiQLService.formQuery(graphiQLQueryObject)
  const reservationResponse = (await EinzaGraphiQLService.getData(mappedQuery))?.data?.findOrder

  return reservationResponse
}

/**
 *
 * @param {string} reservationValidTill
 * @param {string} valuesValidTill
 * @param {[{amount: number, posId: string}]} reservationArticles
 * @param {[{amount: number, posId: string}]} valuesArticles
 */
const validateChanges = (reservationValidTill, valuesValidTill, reservationArticles, valuesArticles, hasDraftItems) => {
  if (reservationArticles.length === 0) {
    throw new Error("NO_ARTICLES")
  }

  const now = new Date().setHours(0, 0, 0, 0)
  const reservationValidTillDate = new Date(reservationValidTill).setHours(0, 0, 0, 0)
  const valuesValidTillDate = new Date(valuesValidTill).setHours(0, 0, 0, 0)

  const sameDates = reservationValidTillDate === valuesValidTillDate

  if (valuesValidTillDate < now) {
    throw new Error("VALID_TILL_PAST")
  }

  if (valuesArticles.length === 0) {
    return
  }

  let hasChanges = false
  for (let i = 0; i < valuesArticles.length; i++) {
    const reservationArticle = reservationArticles.find((article) => article.posId === valuesArticles[i].posId)

    if (!reservationArticle) {
      throw new Error("ARTICLE_NOT_FOUND")
    }

    if (hasChanges) {
      continue
    } else {
      hasChanges = reservationArticle.amount !== valuesArticles[i].amount
    }
  }

  if (reservationArticles.length !== valuesArticles.length || hasChanges || !sameDates || hasDraftItems) {
    return
  }

  throw new Error("NO_CHANGES")
}
